import { designTokens } from '@/utils/design-tokens'
import { useShowTasks } from '@/hooks/useShowTasks'
import type { StrategySummaryMetrics } from '@/api/generated/models/StrategySummaryMetrics'
import RateBar from './RateBar'
import {
  METRIC_META,
  getMetricKeys,
  getRate,
  strategyColors,
} from '../strategyMetrics'

export interface SummaryCardItem {
  id: string
  title: string
  /** 제목 위 작은 라벨 (예: "전략과제 1") */
  eyebrow?: string
  /** 막대 아래 보조 문구 (예: "세부실행과제 3개 · 수행업무 12개") */
  footer?: string
  metrics: StrategySummaryMetrics
  /** dark: 진한 배경 카드 (사업 선택의 '전체') */
  tone?: 'dark'
}

interface SummaryCardsProps {
  items: SummaryCardItem[]
  selectedId: string | null
  onSelect: (id: string) => void
  /** business: 사업 선택 카드 / task-group: 전략과제 카드 */
  variant: 'business' | 'task-group'
}

const CARDS_PER_ROW = 5

/** 지표 막대(세부실행과제 표시 여부에 따라 3~4개)가 들어간 선택 카드 목록 (사업 선택, 전략과제별 성과) — 한 줄에 5개 */
export default function SummaryCards({
  items,
  selectedId,
  onSelect,
  variant,
}: SummaryCardsProps) {
  const font = designTokens.typography.fontFamily.sans[0]
  const isBusiness = variant === 'business'
  const metricKeys = getMetricKeys(useShowTasks())
  // 마지막 줄의 빈 칸은 '해당 없음' 칸처럼 점선 칸으로 채운다
  const emptyCount =
    (CARDS_PER_ROW - (items.length % CARDS_PER_ROW)) % CARDS_PER_ROW

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${CARDS_PER_ROW}, minmax(0, 1fr))`,
        gap: isBusiness ? 12 : 16,
      }}
    >
      {items.map((item) => {
        const selected = item.id === selectedId
        const isDark = item.tone === 'dark'
        const selectedBorder =
          isBusiness && !isDark
            ? `2px solid ${strategyColors.text}`
            : `2px solid ${isDark ? strategyColors.link : strategyColors.selected}`
        const subTextColor = isDark
          ? strategyColors.darkSubText
          : strategyColors.subText
        return (
          <button
            key={item.id}
            type="button"
            aria-pressed={selected}
            onClick={() => onSelect(item.id)}
            style={{
              textAlign: 'left',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              padding: isBusiness ? '14px 16px' : 18,
              borderRadius: 12,
              background: isDark
                ? strategyColors.darkSurface
                : strategyColors.surface,
              fontFamily: font,
              color: isDark ? strategyColors.surface : strategyColors.text,
              border: selected
                ? selectedBorder
                : `1px solid ${isDark ? strategyColors.darkSurface : strategyColors.border}`,
              margin: selected ? 0 : 1,
              boxShadow:
                selected && (!isBusiness || isDark)
                  ? `0 0 0 3px ${strategyColors.selectedRing}`
                  : undefined,
            }}
          >
            {item.eyebrow && (
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: subTextColor,
                }}
              >
                {item.eyebrow}
              </span>
            )}
            <span style={{ fontSize: 20, fontWeight: 700 }}>{item.title}</span>
            <span
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                width: '100%',
              }}
            >
              {metricKeys.map((key) => (
                <RateBar
                  key={key}
                  variant="card"
                  label={METRIC_META[key].short}
                  rate={getRate(item.metrics, key)}
                  inverse={isDark}
                />
              ))}
            </span>
            {item.footer && (
              <span style={{ fontSize: 14, color: subTextColor }}>
                {item.footer}
              </span>
            )}
          </button>
        )
      })}
      {Array.from({ length: emptyCount }, (_, index) => (
        <div
          key={`empty-${index}`}
          aria-hidden="true"
          style={{
            minHeight: isBusiness ? 120 : 160,
            borderRadius: 12,
            background: strategyColors.naSurface,
            border: `1px dashed ${strategyColors.dashed}`,
            margin: 1,
          }}
        />
      ))}
    </div>
  )
}
